import { useMemo } from 'react'
import type { User } from '@/types/user'
import {
  UserRole,
  INTERFACE_PERMISSION,
  ORG_PERMISSION,
  IMyAccessControl,
} from '@/types/user'
import useUser from '@/features/auth/hooks/useUser'
import useMyAccessControl from '@/features/auth/hooks/useMyAccessControl'
import { useAuthContext } from '@/features/auth/context'
import _ from 'lodash'

export type AdminAccessUser = User | null | undefined

export const hasAdminAccess = (user: AdminAccessUser): boolean =>
  [UserRole.Admin, UserRole.SuperAdmin].includes(user?.role as UserRole)

const useAdminAccess = (user: AdminAccessUser): boolean => {
  return useMemo(() => hasAdminAccess(user), [user])
}

const useHasPermissionForAnyOrganization = (
  orgPermissions: IMyAccessControl['organizations'],
) => {
  return useMemo(() => {
    return orgPermissions.some((item) =>
      item.permissions.includes(ORG_PERMISSION.MANAGE),
    )
  }, [orgPermissions])
}

export const usePermissionAccess = () => {
  const userQuery = useUser()
  let { interfaceId } = useAuthContext()
  const userData = userQuery.data
  const isAdminAccess = useAdminAccess(userData)
  const {
    data: myAccessControl,
    isInitialLoading: isAccessControlLoading,
    isLoading: isLoadingMyAccessControl,
  } = useMyAccessControl()

  const interfacePermissions = useMemo(() => {
    return (
      _.flatten(
        myAccessControl?.organizations.map((item) => item.interfaces),
      ) || []
    )
  }, [myAccessControl])

  const orgPermissions = useMemo(() => {
    return _.flatten(myAccessControl?.organizations) || []
  }, [myAccessControl])

  const getPermissionInterfaceById = (id: number) => {
    const list =
      interfacePermissions.find((item) => Number(item.id) === Number(id))
        ?.permissions || []
    return {
      isRead: isAdminAccess || list.includes(INTERFACE_PERMISSION.READ),
      isWrite: isAdminAccess || list.includes(INTERFACE_PERMISSION.WRITE),
      isAdmin: isAdminAccess || list.includes(INTERFACE_PERMISSION.ADMIN),
      isDelete: isAdminAccess || list.includes(INTERFACE_PERMISSION.DELETE),
      isViewOnly:
        !list.length ||
        (list.length === 1 && list[0] === INTERFACE_PERMISSION.READ),
    }
  }

  const getPermissionOrgById = (id: number) => {
    const list =
      orgPermissions.find((item) => Number(item.id) === Number(id))
        ?.permissions || []
    return {
      isRead: isAdminAccess || list.includes(ORG_PERMISSION.READ),
      isWrite: isAdminAccess || list.includes(ORG_PERMISSION.WRITE),
      isAdmin: isAdminAccess || list.includes(ORG_PERMISSION.MANAGE),
      isDelete: isAdminAccess || list.includes(ORG_PERMISSION.DELETE),
    }
  }
  const hasPermissionForAnyOrganization =
    useHasPermissionForAnyOrganization(orgPermissions)

  // Admin/SuperAdmin (system role from OIDC identity via deriveSystemRole) are
  // never ViewOnly, regardless of per-interface permissions or whether an
  // interface is selected yet. Restores the admin bypass the old Redux
  // fetchUserPermissions path had before usePermission switched to this hook.
  let isViewOnly =
    !isLoadingMyAccessControl && myAccessControl
      ? getPermissionInterfaceById(Number(interfaceId)).isViewOnly
      : false

  if (isAdminAccess) {
    isViewOnly = false
  }
  return {
    ...userQuery,
    userData,
    isAdminAccess,
    isAccessControlLoading,
    getPermissionInterfaceById,
    getPermissionOrgById,
    hasPermissionForAnyOrganization,
    interfacePermission: getPermissionInterfaceById(Number(interfaceId)),
    isLoadingMyAccessControl,
    isViewOnly,
  }
}
